import JsonViewer from 'shared/JsonViewer';
import styled from 'styled-components';
import { Button, Box, Typography, Paper, Chip, useTheme } from '@mui/material';
import { Modal } from 'shared';
import { copyToClipboard, conditionallyRenderElement } from 'utils';
import { FetchPendingEntityStatusResponse } from 'IntegrationModule/types';
import { useEffect } from 'react';
import useLocalDispatch from 'hooks/useLocalDispatch';
import { fetchPendingEntityStatus } from 'thunk';
import { useMount } from 'react-use';

const fetchPendingEntityStatusStateId = 'fetchPendingEntityStatusStateId';

export type PendingEntityStatusModalProps = {
  onClose: () => void;
  open: boolean;
  params: { entityId: number; targetServiceId: number; dataType?: string };
};

const Container = styled.div`
  overflow-y: auto;
  max-height: 85vh;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  background-color: ${(props: any) => props.theme.palette.background.paper};
  padding: 16px 0;
  border-top: 1px solid ${(props: any) => props.theme.palette.divider};
  margin-top: 24px;
`;

const SectionTitle = styled(Typography)`
  font-weight: 600;
  margin-bottom: 12px;
  color: ${(props: any) => props.theme.palette.text.primary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const PendingEntityStatusModal = ({
  onClose,
  open,
  params,
}: PendingEntityStatusModalProps) => {
  const { dispatchLocal, selectLocalData } = useLocalDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useMount(() => {
    dispatchLocal(fetchPendingEntityStatus, {
      params,
      stateId: fetchPendingEntityStatusStateId,
    });
  });

  const { data, isRequesting, error } = selectLocalData<FetchPendingEntityStatusResponse>({
    stateId: fetchPendingEntityStatusStateId,
  });

  const renderJsonSection = (title: string, jsonData: any, chipLabel?: string) => {
    if (!jsonData) return null;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(248, 250, 252, 0.8)',
          border: isDark
            ? '1px solid rgba(148, 163, 184, 0.12)'
            : '1px solid rgba(226, 232, 240, 0.6)',
          borderRadius: '12px',
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
          },
        }}
      >
        <SectionTitle variant="h6">
          {title}
          {chipLabel && (
            <Chip
              label={chipLabel}
              size="small"
              color="primary"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          )}
        </SectionTitle>
        <Box
          sx={{
            // react-json-view renders long token-like strings (ids, hashes) with no
            // break opportunities; these inherited properties let them wrap in place
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            '& .react-json-view': {
              backgroundColor: 'transparent !important',
              padding: '0 !important',
            },
          }}
        >
          <JsonViewer
            name={false}
            src={jsonData}
            collapsed={2}
            displayDataTypes={false}
            displayObjectSize={true}
            enableClipboard={copyToClipboard}
            style={{
              fontSize: '0.875rem',
              lineHeight: 1.6,
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            }}
          />
        </Box>
      </Paper>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Container>
        <Box
          sx={{
            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            Entity ID: {params.entityId}
            <Chip
              label={`Target Service: ${params.targetServiceId}`}
              size="small"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            />
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Pending Entities By Data Type and Pending Mapping Status
          </Typography>
        </Box>

        {conditionallyRenderElement({
          isLoading: isRequesting,
          error: error?.message,
          content: data && (
            <Box>
              {renderJsonSection('General Data', (data as any).general_data, 'Metadata')}
              {renderJsonSection('Mosaic Data', (data as any).mosaic_data, 'Source')}
              {renderJsonSection('Target Data', (data as any).target_data, 'Destination')}

              {/* Fallback: Show full data if structured format not available */}
              {!(data as any).general_data &&
                !(data as any).mosaic_data &&
                !(data as any).target_data &&
                renderJsonSection('Entity Details', data)}
            </Box>
          ),
        })}

        <ButtonContainer>
          <Button
            variant="contained"
            color="primary"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
            }}
          >
            Close
          </Button>
        </ButtonContainer>
      </Container>
    </Modal>
  );
};
